/**
 * Workout History Page
 *
 * Full-page grid/list of saved workouts with search, filter, and detail views.
 * Pattern follows WorkoutGeneratorPage.jsx conventions.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Activity,
  Calendar,
  ChevronDown,
  ChevronUp,
  Dumbbell,
  Loader2,
  Search,
  Trash2,
  Clock,
  Target,
} from 'lucide-react';
import { getUser } from '../../utils/helpers';
import { NODE_BACKEND_URL } from '../../utils/constant';

const cardClass = 'rounded-2xl border border-white/10 bg-[#0b1118]';

export default function WorkoutHistoryPage() {
  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const user = getUser();
  const userId = user?.id || user?._id || 'anonymous';

  const loadWorkouts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${NODE_BACKEND_URL}/api/strength/user/${userId}/workouts`);
      if (!response.ok) throw new Error('Failed to load workouts');
      const data = await response.json();
      setWorkouts(data.result || []);
    } catch (err) {
      console.error('[WorkoutHistory] Load error:', err);
      setError(err.message || 'Failed to load workout history');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadWorkouts();
  }, [loadWorkouts]);

  const handleDelete = async (workoutId) => {
    if (!window.confirm('Delete this workout?')) return;
    setDeletingId(workoutId);
    try {
      const response = await fetch(
        `${NODE_BACKEND_URL}/api/strength/user/${userId}/workouts/${workoutId}`,
        { method: 'DELETE' }
      );
      if (!response.ok) throw new Error('Failed to delete workout');
      setWorkouts((prev) => prev.filter((w) => (w.id || w._id) !== workoutId));
      if (expandedId === workoutId) setExpandedId(null);
    } catch (err) {
      console.error('[WorkoutHistory] Delete error:', err);
      setError(err.message || 'Failed to delete workout');
    } finally {
      setDeletingId(null);
    }
  };

  const filteredWorkouts = useMemo(() => {
    if (!searchQuery.trim()) return workouts;
    const q = searchQuery.toLowerCase();
    return workouts.filter((w) => {
      const name = (w.name || '').toLowerCase();
      const level = (w.fitnessLevel || '').toLowerCase();
      const areas = (w.focusAreas || []).join(' ').toLowerCase();
      return name.includes(q) || level.includes(q) || areas.includes(q);
    });
  }, [workouts, searchQuery]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-full bg-[#040608] text-zinc-100 p-4 md:p-6">
      <div className="mx-auto max-w-4xl space-y-4">
        {/* Header */}
        <header className={`${cardClass} p-4`}>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="grid h-9 w-9 place-items-center rounded-xl border border-orange-400/45 bg-orange-400/10 text-orange-300">
                <Activity size={16} />
              </span>
              <div>
                <h1 className="text-[15px] font-semibold tracking-tight">Workout History</h1>
                <p className="text-[10px] text-zinc-500">
                  {workouts.length} saved workout{workouts.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={loadWorkouts}
              disabled={loading}
              className="inline-flex items-center gap-1 rounded-full border border-white/20 px-2 py-1 text-[10px] text-zinc-300 hover:text-white hover:bg-white/[0.06] transition-colors disabled:opacity-50"
            >
              {loading ? <Loader2 size={11} className="animate-spin" /> : <Clock size={11} />}
              Refresh
            </button>
          </div>
        </header>

        {/* Search */}
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search workouts by name, level, or focus area..."
            className="w-full rounded-xl border border-white/10 bg-[#0b1118] pl-9 pr-3 py-2.5 text-[12px] text-zinc-100 placeholder:text-zinc-500 focus:border-orange-400/60 focus:outline-none"
          />
        </div>

        {/* Error */}
        {error && (
          <div className="rounded-xl border border-rose-400/40 bg-rose-500/10 px-3 py-2 text-[11px] text-rose-200">
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Loader2 size={24} className="animate-spin text-orange-400" />
            <p className="text-[12px] text-zinc-500">Loading workout history...</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredWorkouts.length === 0 && (
          <div className={`${cardClass} p-8 text-center`}>
            <Dumbbell size={32} className="mx-auto text-zinc-600 mb-3" />
            <h3 className="text-[14px] font-medium text-zinc-300 mb-1">
              {searchQuery ? 'No matching workouts' : 'No saved workouts yet'}
            </h3>
            <p className="text-[11px] text-zinc-500">
              {searchQuery
                ? 'Try adjusting your search query.'
                : 'Generate a workout in the Workout Generator and save it to see it here.'}
            </p>
          </div>
        )}

        {/* Workout List */}
        {!loading && filteredWorkouts.length > 0 && (
          <div className="space-y-2">
            {filteredWorkouts.map((workout) => {
              const id = workout.id || workout._id;
              const isExpanded = expandedId === id;
              const isDeleting = deletingId === id;

              return (
                <div key={id} className={`${cardClass} overflow-hidden`}>
                  {/* Summary Row */}
                  <button
                    type="button"
                    onClick={() => setExpandedId(isExpanded ? null : id)}
                    className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-white/[0.02] transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-medium text-zinc-100 truncate">
                        {workout.name || 'Untitled Workout'}
                      </p>
                      <div className="flex items-center gap-3 mt-0.5">
                        {workout.createdAt && (
                          <span className="flex items-center gap-1 text-[10px] text-zinc-500">
                            <Calendar size={10} /> {formatDate(workout.createdAt)}
                          </span>
                        )}
                        {workout.fitnessLevel && (
                          <span className="flex items-center gap-1 text-[10px] text-zinc-500">
                            <Target size={10} /> {workout.fitnessLevel}
                          </span>
                        )}
                        {workout.daysPerWeek && (
                          <span className="text-[10px] text-zinc-500">
                            {workout.daysPerWeek} days/week
                          </span>
                        )}
                        {workout.numberOfWeeks && (
                          <span className="text-[10px] text-zinc-500">
                            {workout.numberOfWeeks} weeks
                          </span>
                        )}
                      </div>
                      {workout.focusAreas && workout.focusAreas.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {workout.focusAreas.map((area) => (
                            <span
                              key={area}
                              className="rounded-full border border-white/10 bg-white/[0.04] px-1.5 py-0.5 text-[9px] text-zinc-400"
                            >
                              {area}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-2">
                      <span className="rounded-full border border-orange-400/40 px-2 py-0.5 text-[10px] text-orange-200">
                        {workout.days?.length || workout.daysPerWeek || '?'} days
                      </span>
                      {isExpanded ? (
                        <ChevronUp size={14} className="text-zinc-400" />
                      ) : (
                        <ChevronDown size={14} className="text-zinc-400" />
                      )}
                    </div>
                  </button>

                  {/* Expanded Detail */}
                  {isExpanded && (
                    <div className="border-t border-white/10 px-4 py-3 space-y-2">
                      {workout.days && workout.days.length > 0 ? (
                        workout.days.map((day, idx) => (
                          <div
                            key={idx}
                            className="rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2"
                          >
                            <p className="text-[12px] font-medium text-zinc-100">{day.day || `Day ${idx + 1}`}</p>
                            <p className="text-[10px] text-zinc-500">{day.focus || ''}</p>
                            {day.warmup && (
                              <p className="text-[10px] text-zinc-400 mt-1">
                                <span className="text-orange-300">Warm-up:</span> {day.warmup}
                              </p>
                            )}
                            {day.workout && (
                              <p className="text-[10px] text-zinc-400 mt-0.5">
                                <span className="text-orange-300">Workout:</span> {day.workout}
                              </p>
                            )}
                            {day.strength && (
                              <p className="text-[10px] text-zinc-400 mt-0.5">
                                <span className="text-orange-300">Strength:</span> {day.strength}
                              </p>
                            )}
                            {day.notes && (
                              <p className="text-[10px] text-zinc-500 mt-0.5">Notes: {day.notes}</p>
                            )}
                            {day.exerciseList && day.exerciseList.length > 0 && (
                              <ul className="mt-1 space-y-0.5">
                                {day.exerciseList.map((ex, eIdx) => (
                                  <li key={eIdx} className="text-[10px] text-zinc-400">
                                    {ex.name || ex} {ex.sets ? `- ${ex.sets} x ${ex.reps}` : ''}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        ))
                      ) : (
                        <p className="text-[11px] text-zinc-500">No day-by-day detail available.</p>
                      )}

                      <div className="flex justify-end pt-1">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleDelete(id); }}
                          disabled={isDeleting}
                          className="inline-flex items-center gap-1 rounded-lg border border-rose-400/30 bg-rose-500/10 px-2.5 py-1 text-[10px] text-rose-300 hover:bg-rose-500/20 transition-colors disabled:opacity-50"
                        >
                          {isDeleting ? <Loader2 size={10} className="animate-spin" /> : <Trash2 size={10} />}
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
